
"use client";

import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Input } from "../ui/input";
import { UserCheck, Plus, Edit, Info, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AssignApproverModal } from "../modals/assign-approver-modal";
import { EditApproverModal } from "../modals/edit-approver-modal";
import { Pagination } from "../common/pagination-dynamic";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { useState, useEffect, useMemo, ChangeEvent } from "react";
import axios from "axios";
import { format } from "date-fns";

interface AssignedEmployee {
  userId: number;
  userName: string;
  primaryApproverName: string;
  secondaryApproverName: string;
  modUserName: string;
  modifiedOn: string;
}

interface User {
  userID?: number;
  UserID?: number;
  userName?: string;
  UserName?: string;
}

interface Manager {
  id?: number;
  name?: string;
  userID?: number;
  UserID?: number;
  userName?: string;
  UserName?: string;
}

export function AssignApproverPage() {
  const [assignedEmployees, setAssignedEmployees] = useState<AssignedEmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<AssignedEmployee | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const [roles, setRoles] = useState<string[]>([]);
  const [userId, setUserId] = useState<number | null>(null);

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof AssignedEmployee | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch assigned employees on page load
  useEffect(() => {
    const storedRoles = localStorage.getItem("roles");
    const storedUserId = localStorage.getItem("id");

    if (storedRoles) setRoles(JSON.parse(storedRoles));
    if (storedUserId) setUserId(Number(storedUserId));

    const fetchAssignedEmployees = async () => {
      setLoading(true);
      try {
        const res = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/getallusermanagerinfo", { withCredentials: true });
        const normalized: AssignedEmployee[] = (res.data ?? []).map((r: any) => ({
          userId: r.userID ?? r.userId ?? r.id,
          userName: r.userName ?? r.user_name ?? "",
          primaryApproverName: r.primaryManagerName ?? "",
          secondaryApproverName: r.secondaryManagerName ?? "--",
          modUserName: r.modUserName ?? "",
          modifiedOn: r.modifiedOn ?? "",
        }));
        setAssignedEmployees(normalized);
      } catch (err: any) {
        console.error("Error fetching assigned employees:", err);
        setError("Failed to load assigned employees.");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedEmployees();
  }, []);

  // Individual fetch functions for each dropdown
  const fetchUsers = async (): Promise<{ id: number; name: string }[]> => {
    try {
      const usersRes = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/getnoapproverlist", { withCredentials: true });
      const users = (usersRes.data ?? []).map((u: any) => ({ 
        id: u.userID ?? u.UserID ?? 0, 
        name: u.userName ?? u.UserName ?? "" 
      }));
      return users;
    } catch (err: any) {
      console.error("Error fetching users:", err);
      return [];
    }
  };

  const fetchManagers = async (): Promise<{ id: number; name: string }[]> => {
    try {
      const primaryRes = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/getallmanagers", { withCredentials: true });
      const managers = (primaryRes.data ?? []).map((m: any) => ({ 
        id: m.userID ?? m.UserID ?? 0, 
        name: m.userName ?? m.UserName ?? "" 
      }));
      return managers;
    } catch (err: any) {
      console.error("Error fetching managers:", err);
      return [];
    }
  };

  const fetchSecondaryUsers = async (): Promise<{ id: number; name: string }[]> => {
    try {
      const secondaryRes = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/GetAllUsers", { withCredentials: true });
      const users = (secondaryRes.data ?? []).map((u: any) => ({ 
        id: u.userID ?? u.UserID ?? 0, 
        name: u.userName ?? u.UserName ?? "" 
      }));
      return users;
    } catch (err: any) {
      console.error("Error fetching secondary users:", err);
      return [];
    }
  };

  const handleSaveApprover = async (formData: any) => {
    try {
      const selectedUserId = Number(formData.user ?? 0);
      const selectedPrimaryApproverId = Number(formData.primaryApprover) || 0;
      const selectedSecondaryApproverId = formData.secondaryApprover === "none" ? null : Number(formData.secondaryApprover) || null;
      const modUserId = userId ?? 0;

      if (!selectedUserId || !selectedPrimaryApproverId) {
        return;
      }

      const payload = {
        UserID: selectedUserId,
        PrimaryManagerID: selectedPrimaryApproverId,
        SecondaryManagerID: selectedSecondaryApproverId,
        ModUserID: modUserId
      };

      const res = await axios.post(process.env.REACT_APP_API_URL +"/api/HRAdmin/save", payload, { headers: { "Content-Type": "application/json" }, withCredentials: true });

      if (res.data.status === 1) {
        const assignedRes = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/getallusermanagerinfo", { withCredentials: true });
        const normalized: AssignedEmployee[] = (assignedRes.data ?? []).map((r: any) => ({
          userId: r.userID ?? r.userId ?? r.id,
          userName: r.userName ?? r.user_name ?? "",
          primaryApproverName: r.primaryManagerName ?? "",
          secondaryApproverName: r.secondaryManagerName ?? "--",
          modUserName: r.modUserName ?? "",
          modifiedOn: r.modifiedOn ?? "",
        }));
        setAssignedEmployees(normalized);
        setIsAssignModalOpen(false);
        setPopupMessage("Approver saved successfully");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 3000);
      } else {
        setPopupMessage("Failed to save approver: " + (res.data.message || "Unknown error"));
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
      }
    } catch (err: any) {
      console.error("Error saving approver:", err);
      setPopupMessage("Error saving approver. Please try again.");
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  // Edit approver
  const handleEditSave = async (formData: any) => {
    try {
      if (!selectedEmployee) return;

      const selectedPrimaryApproverId = formData.primaryApprover;
      const selectedSecondaryApproverId =
  formData.secondaryApprover === "none" || !formData.secondaryApprover
    ? null
    : Number(formData.secondaryApprover);
      const modUserId = userId ?? 0;

      if (!selectedPrimaryApproverId) {
        return;
      }

      const payload = {
        UserID: Number(selectedEmployee.userId),
        PrimaryManagerID: Number(selectedPrimaryApproverId),
        SecondaryManagerID: selectedSecondaryApproverId,
        ModUserID: modUserId
      };

      const res = await axios.post(process.env.REACT_APP_API_URL +"/api/HRAdmin/save", payload, { headers: { "Content-Type": "application/json" }, withCredentials: true });

      if (res.data.status === 1) {
        const assignedRes = await axios.get(process.env.REACT_APP_API_URL +"/api/HRAdmin/getallusermanagerinfo", { withCredentials: true });
        const normalized: AssignedEmployee[] = (assignedRes.data ?? []).map((r: any) => ({
          userId: r.userID ?? r.userId ?? r.id,
          userName: r.userName ?? r.user_name ?? "",
          primaryApproverName: r.primaryManagerName ?? "",
          secondaryApproverName: r.secondaryManagerName ?? "--",
          modUserName: r.modUserName ?? "",
          modifiedOn: r.modifiedOn ?? "",
        }));
        setAssignedEmployees(normalized);
        setIsEditModalOpen(false);
        setPopupMessage("Approver updated successfully");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 3000);
      } else {
        setPopupMessage("Failed to update approver: " + (res.data.message || "Unknown error"));
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 3000);
      }
    } catch (err: any) {
      console.error("Error updating approver:", err);
      setPopupMessage("Error updating approver. Please try again.");
      setShowErrorPopup(true);
      setTimeout(() => setShowErrorPopup(false), 3000);
    }
  };

  // Filter and sort employees by priority
  const filteredEmployees = useMemo(() => {
    if (!searchTerm.trim()) return assignedEmployees;
    
    const term = searchTerm.toLowerCase();
    
    // Filter employees that match the search
    const matching = assignedEmployees.filter((employee) => {
      return (
        employee.userName?.toLowerCase().includes(term) ||
        employee.primaryApproverName?.toLowerCase().includes(term) ||
        employee.secondaryApproverName?.toLowerCase().includes(term) ||
        employee.modUserName?.toLowerCase().includes(term)
      );
    });
    
    // Sort by priority:
    // 1. Employee name matches (highest priority - score 0)
    // 2. Primary approver matches (score 1)
    // 3. Secondary approver matches (lowest priority - score 2)
    return matching.sort((a, b) => {
      const getPriority = (employee: AssignedEmployee) => {
        if (employee.userName?.toLowerCase().includes(term)) return 0;
        if (employee.primaryApproverName?.toLowerCase().includes(term)) return 1;
        if (employee.secondaryApproverName?.toLowerCase().includes(term)) return 2;
        return 3; // Should not happen since we filtered
      };
      
      return getPriority(a) - getPriority(b);
    });
  }, [assignedEmployees, searchTerm]);

  // Sorting function
  const handleSort = (field: keyof AssignedEmployee) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field: keyof AssignedEmployee) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />;
  };

  // Apply sorting to filtered employees
  const sortedAndFilteredEmployees = useMemo(() => {
    let result = [...filteredEmployees];
    
    if (sortField) {
      result.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (sortField) {
          case 'userName':
          case 'primaryApproverName':
          case 'secondaryApproverName':
          case 'modUserName':
            aValue = (a[sortField] || '').toLowerCase();
            bValue = (b[sortField] || '').toLowerCase();
            break;
          case 'modifiedOn':
            aValue = new Date(a.modifiedOn || 0).getTime();
            bValue = new Date(b.modifiedOn || 0).getTime();
            break;
          case 'userId':
            aValue = a.userId;
            bValue = b.userId;
            break;
          default:
            // This should never happen as all fields are handled above
            aValue = String(a[sortField as keyof AssignedEmployee] || '').toLowerCase();
            bValue = String(b[sortField as keyof AssignedEmployee] || '').toLowerCase();
        }
        
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }
        
        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }
    
    return result;
  }, [filteredEmployees, sortField, sortDirection]);

  const totalPages = Math.ceil(sortedAndFilteredEmployees.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentEmployees = sortedAndFilteredEmployees.slice(startIndex, endIndex);

  // Helper function to format date and time
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "--";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "--";
      return format(date, "dd-MM-yyyy HH:mm");
    } catch {
      return "--";
    }
  };

  if (loading) return <p className="p-4">Loading...</p>;
  if (error) return <p className="p-4 text-red-500">{error}</p>;

  return (
    <div className={`p-6 space-y-6 text-gray-900 bg-gray-50`}>
      {/* Success Popup */}
      {showSuccessPopup && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "white",
            color: "#059669",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
            zIndex: 1000,
            border: "2px solid #10b981",
            maxWidth: "400px",
          }}
        >
          {popupMessage}
        </div>
      )}

      {/* Error Popup */}
      {showErrorPopup && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            backgroundColor: "white",
            color: "#dc2626",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
            zIndex: 1000,
            border: "2px solid #ef4444",
            maxWidth: "400px",
          }}
        >
          {popupMessage}
        </div>
      )}

      {/* Card Table */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-white p-6">
          
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <UserCheck className="h-6 w-6 text-blue-600" /> Assign Approver
            </CardTitle>
            <Button
              onClick={() => {
                setIsAssignModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Assign New
            </Button>
          </div>

          {/* Rules & Guidelines */}
      <Collapsible open={isRulesOpen} onOpenChange={setIsRulesOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 p-0 h-auto text-blue-600 hover:text-blue-700">
            <Info className="w-4 h-4" />
            <span className="font-medium">Assignee Guidelines</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isRulesOpen ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className={`p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-800`}>
            <ul className="space-y-2 text-sm">
              <li>• User and Primary Approver are mandatory while Assigning.</li>
              <li>• Only Managers can be Primary Approvers.</li>
              <li>• User cannot be their own Approver (Primary or Secondary).</li>
              <li>• Primary and Secondary Approver cannot be same.</li>
            </ul>
          </div>
        </CollapsibleContent>
      </Collapsible>

          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <Input
                placeholder="Search by employee or approver name..."
                value={searchTerm}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-3 pr-3 py-2"
              />
            </div>
            {searchTerm && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                  className="whitespace-nowrap"
                >
                  Clear Search
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        

        <CardContent className="p-0">
          <div className="overflow-y-auto max-h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 border-b border-gray-100">
                  <TableHead className="w-16 font-semibold text-gray-700 p-4">#</TableHead>
                  <TableHead 
                    className="font-semibold text-gray-700 p-4 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('userName')}
                  >
                    <div className="flex items-center gap-2">
                      Employee Name
                      {getSortIcon('userName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-gray-700 p-4 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('primaryApproverName')}
                  >
                    <div className="flex items-center gap-2">
                      Primary Approver
                      {getSortIcon('primaryApproverName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-gray-700 p-4 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('secondaryApproverName')}
                  >
                    <div className="flex items-center gap-2">
                      Secondary Approver
                      {getSortIcon('secondaryApproverName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-gray-700 p-4 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('modUserName')}
                  >
                    <div className="flex items-center gap-2">
                      Modified By
                      {getSortIcon('modUserName')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="font-semibold text-gray-700 p-4 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('modifiedOn')}
                  >
                    <div className="flex items-center gap-2">
                      Modified On
                      {getSortIcon('modifiedOn')}
                    </div>
                  </TableHead>
                  <TableHead className="w-32 font-semibold text-gray-700 p-4">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentEmployees.map((employee, index) => (
                  <TableRow key={`${employee.userId}-${startIndex + index}`} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                    <TableCell className="font-medium text-gray-600 p-4">{startIndex + index + 1}</TableCell>
                    <TableCell className="font-medium text-gray-900 p-4">{employee.userName}</TableCell>
                    <TableCell className="text-gray-700 p-4">{employee.primaryApproverName || "—"}</TableCell>
                    <TableCell className="text-gray-700 p-4">{employee.secondaryApproverName || "--"}</TableCell>
                    <TableCell className="text-gray-700 p-4">{employee.modUserName || "--"}</TableCell>
                    <TableCell className="text-gray-700 p-4">{formatDateTime(employee.modifiedOn)}</TableCell>
                    <TableCell className="p-4">
                      <Button size="sm" variant="outline" onClick={() => { 
                        setSelectedEmployee(employee);
                        setIsEditModalOpen(true);
                      }} className="flex items-center gap-1.5">
                        <Edit className="h-3.5 w-3.5" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={sortedAndFilteredEmployees.length}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      <AssignApproverModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSave={handleSaveApprover}
        onFetchUsers={fetchUsers}
        onFetchManagers={fetchManagers}
        onFetchSecondaryUsers={fetchSecondaryUsers}
      />

      {selectedEmployee && (
        <EditApproverModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleEditSave}
          employee={selectedEmployee}
          onFetchManagers={fetchManagers}
          onFetchSecondaryUsers={fetchSecondaryUsers}
        />
      )}
    </div>
  );
}
